import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChefHat, RefreshCw, ArrowLeft, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NEXT_ACTION = {
  new: { label: "Start Preparing", next: "preparing", cls: "bg-[#F57C00] text-black hover:bg-[#FF9800]" },
  preparing: { label: "Mark Ready", next: "ready", cls: "bg-[#388E3C] text-white hover:bg-[#4CAF50]" },
  ready: { label: "Complete & Clear", next: "completed", cls: "bg-zinc-600 text-white hover:bg-zinc-500" },
};

const STATUS_LABEL = { new: "NEW", preparing: "PREPARING", ready: "READY" };

const elapsed = (iso) => {
  const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  return mins < 1 ? "just now" : `${mins} min ago`;
};

const Kitchen = () => {
  const token = localStorage.getItem("podi_admin_token") || "";
  const [orders, setOrders] = useState(null);
  const [denied, setDenied] = useState(false);
  const knownRef = useRef(null);
  const [, setTick] = useState(0);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchOrders = useCallback(async () => {
    if (!token) {
      setDenied(true);
      return;
    }
    try {
      const res = await axios.get(`${API}/orders`, { headers });
      const active = res.data
        .filter((o) => ["new", "preparing", "ready"].includes(o.status))
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      const prev = knownRef.current;
      if (prev !== null) {
        const fresh = active.filter((o) => !prev.has(o.id) && o.status === "new");
        if (fresh.length > 0) {
          toast.success(`New order ${fresh[0].order_no} — ${fresh[0].items.length} item line(s)`);
        }
      }
      knownRef.current = new Set(res.data.map((o) => o.id));
      setOrders(active);
    } catch (e) {
      if (e.response?.status === 401) setDenied(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    fetchOrders();
    const poll = setInterval(fetchOrders, 12000);
    const clock = setInterval(() => setTick((t) => t + 1), 30000);
    return () => {
      clearInterval(poll);
      clearInterval(clock);
    };
  }, [fetchOrders]);

  const advance = async (order) => {
    const action = NEXT_ACTION[order.status];
    if (!action) return;
    try {
      await axios.patch(`${API}/orders/${order.id}/status`, { status: action.next }, { headers });
      fetchOrders();
    } catch {
      toast.error("Could not update order");
    }
  };

  if (denied) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#0A0A0C] px-5 text-center text-zinc-300">
        <ChefHat size={40} className="text-[#F57C00]" />
        <p className="font-display text-2xl">Kitchen access needs a staff login</p>
        <a
          data-testid="kitchen-login-link"
          href="/admin"
          className="rounded-full bg-[#F57C00] px-6 py-3 font-grotesk text-sm font-semibold text-black hover:bg-[#FF9800]"
        >
          Go to Staff Login
        </a>
      </div>
    );
  }

  return (
    <div data-testid="kitchen-display" className="min-h-screen bg-black text-[#F5F2EB]">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-black/90 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5">
          <div className="flex items-center gap-3">
            <img src="/assets/mark.png" alt="" className="h-9 w-9 rounded-full object-cover" />
            <div>
              <p className="font-grotesk text-base font-bold tracking-wide">KITCHEN DISPLAY</p>
              <p className="font-mono-accent text-[10px] uppercase tracking-widest text-zinc-500">
                Podi Sambaram • auto-refresh 12s
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span data-testid="kitchen-active-count" className="rounded-full border border-[#F57C00]/40 bg-[#F57C00]/10 px-4 py-1.5 font-mono-accent text-sm font-semibold text-[#FFB74D]">
              {orders ? orders.length : "…"} active
            </span>
            <button
              data-testid="kitchen-refresh-button"
              onClick={fetchOrders}
              className="rounded-full border border-white/10 p-2.5 text-zinc-300 hover:border-[#F57C00]/50 hover:text-[#FF9800]"
              aria-label="Refresh orders"
            >
              <RefreshCw size={15} />
            </button>
            <a
              data-testid="kitchen-back-link"
              href="/admin"
              className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-xs text-zinc-300 hover:border-[#F57C00]/50 hover:text-[#FF9800]"
            >
              <ArrowLeft size={13} /> Admin
            </a>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-8">
        {orders === null ? (
          <p className="mt-20 text-center text-zinc-500">Loading orders...</p>
        ) : orders.length === 0 ? (
          <div data-testid="kitchen-empty" className="mt-24 flex flex-col items-center gap-4 text-center">
            <ChefHat size={48} className="text-zinc-700" />
            <p className="font-display text-3xl text-zinc-400">All clear — no active orders</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {orders.map((o) => {
              const action = NEXT_ACTION[o.status];
              return (
                <article
                  key={o.id}
                  data-testid={`kitchen-order-${o.order_no}`}
                  className={`card-surface flex flex-col rounded-3xl border-2 p-6 ${
                    o.status === "new" ? "order-glow" : "border-white/10"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono-accent text-3xl font-bold tracking-wide text-[#FFB74D]">
                      {o.order_no}
                    </span>
                    <span
                      className={`rounded-full px-3 py-1 font-mono-accent text-[11px] font-bold uppercase tracking-widest ${
                        o.status === "new"
                          ? "bg-sky-500/20 text-sky-300"
                          : o.status === "preparing"
                            ? "bg-amber-500/20 text-amber-300"
                            : "bg-emerald-500/20 text-emerald-300"
                      }`}
                    >
                      {STATUS_LABEL[o.status]}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-zinc-400">
                    <span className="flex items-center gap-1.5">
                      <Clock size={13} /> {elapsed(o.created_at)}
                    </span>
                    <span>
                      {o.order_type === "dine_in"
                        ? `Dine-In${o.table_number ? ` • Table ${o.table_number}` : ""}`
                        : o.delivery_zone === "beyond_3km"
                          ? "Delivery • Beyond 3 KM"
                          : o.delivery_zone === "within_3km"
                            ? "Delivery • Within 3 KM"
                            : "Takeaway / Pickup"}
                    </span>
                    <span
                      className={`font-mono-accent text-xs font-semibold uppercase ${
                        o.payment_status === "paid" ? "text-[#81C784]" : "text-amber-300"
                      }`}
                    >
                      {o.payment_status === "paid" ? "Paid" : "Payment pending"}
                    </span>
                  </div>
                  <ul className="mt-4 flex-1 space-y-2 border-t border-white/10 pt-4">
                    {o.items.map((i) => (
                      <li key={i.name} className="flex items-baseline gap-3">
                        <span className="font-mono-accent text-2xl font-bold text-[#FF9800]">{i.qty}×</span>
                        <span className="font-grotesk text-xl font-medium text-zinc-100">{i.name}</span>
                      </li>
                    ))}
                  </ul>
                  {o.notes && (
                    <p className="mt-3 rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-3 py-2 text-sm italic text-[#D4AF37]">
                      "{o.notes}"
                    </p>
                  )}
                  {action && (
                    <button
                      data-testid={`kitchen-action-${o.order_no}`}
                      onClick={() => advance(o)}
                      className={`mt-5 w-full rounded-2xl py-4 font-grotesk text-lg font-bold transition-all ${action.cls}`}
                    >
                      {action.label}
                    </button>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default Kitchen;
